/**
 * The one error envelope (`docs/design/00-contracts.md` §12.3, C-26).
 *
 * Every non-2xx response in Tvitvi has exactly this shape. `code` is machine-readable, stable
 * and SCREAMING_SNAKE — **that** is what the frontend translates, through
 * `errors.code.<camelCase(code)>`. `message` is English, for developers and logs, and is never
 * rendered to a user: a server-produced sentence in the UI would break principle 8.
 */
import { z } from 'zod';

export const errorDetailSchema = z
  .object({
    /** Dot path into the request, or 'action' for a policy denial. */
    path: z.string(),
    /** The Zod issue code, or 'permission_denied'. */
    code: z.string(),
    /** English, developer-facing. */
    message: z.string(),
  })
  .meta({ id: 'ErrorDetail' });

export const errorResponseSchema = z
  .object({
    error: z.object({
      /** Machine-readable, stable, SCREAMING_SNAKE. THIS is what the frontend translates. */
      code: z.string(),
      /** English, for developers and logs. Never localised, never rendered to a user. */
      message: z.string(),
      requestId: z.string(),
      details: z.array(errorDetailSchema).optional(),
      /**
       * `AI_CONFIRMATION_REQUIRED` (revisjonen 02.11): tokenet og nøyaktig hva som skal skje. AI-en viser det
       * til brukeren og sender det samme kallet på nytt med `X-Tvitvi-Confirmation: <token>`.
       */
      confirmation: z
        .object({
          token: z.string(),
          expiresAt: z.string(),
          method: z.string(),
          path: z.string(),
          body: z.unknown(),
        })
        .optional(),
    }),
  })
  .meta({ id: 'ErrorResponse' });

export type ErrorDetail = z.infer<typeof errorDetailSchema>;
export type ErrorResponse = z.infer<typeof errorResponseSchema>;

export const ERROR_CODES = [
  'VALIDATION_FAILED',
  'INVALID_CURSOR',
  'INVALID_HEADER', // 400
  /* docs/EKSPORT.md: forespørselen passer ikke katalogen (400). */
  'EXPORT_UNKNOWN',
  'EXPORT_FORMAT_NOT_ALLOWED',
  'EXPORT_SCOPE_NOT_ALLOWED',
  'EXPORT_PARAM_MISSING',
  'EXPORT_EMPTY',
  /* Fra definisjonene (agent E4): et filter listas skjema avviser, og et utvalg over taket. */
  'EXPORT_FILTER_INVALID',
  'EXPORT_TOO_LARGE',
  'UNAUTHENTICATED',
  'TOKEN_EXPIRED',
  'TOKEN_INVALID',
  'ACCOUNT_DISABLED',
  'PERSON_NOT_PROVISIONED', // 401
  'FORBIDDEN',
  'CONSENT_REQUIRED',
  'NOT_A_MEMBER',
  'ORGANIZATION_FROZEN', // 0480: medlem av en fryst organisasjon
  'ACCOUNT_CLOSING', // 284.5 (1151): kontoen venter på slettingen; bare «Angre» og /v1/me er åpne
  'EMAIL_NOT_VERIFIED', // 403
  /* B3: tokens utenfra (AI og integrasjoner) — organisasjonens brytere og sperrelista. */
  'AI_ACCESS_DISABLED',
  'AI_WRITE_DISABLED',
  'EXTERNAL_BLOCKED',
  'EXTERNAL_DRAFT_ONLY',
  /* Revisjonen 02.11: godkjenning i to steg. 428 med token; et ugyldig eller brukt token er 403. */
  'AI_CONFIRMATION_REQUIRED',
  'AI_CONFIRMATION_INVALID',
  /* 1335: å slå AI på krever at superadmin bekrefter at organisasjonen har vurdert det. */
  'AI_ACKNOWLEDGEMENT_REQUIRED', // 400
  'EXTERNAL_ORGANIZATION_NOT_GRANTED', // 403
  'NOT_FOUND', // 404
  'ALREADY_EXISTS',
  'CONCURRENT_MODIFICATION',
  'ORGANIZATION_REQUIRED',
  'LAST_SUPERADMIN', // 409
  /* §8.5: samme `Idempotency-Key` med en annen kropp (409). */
  'IDEMPOTENCY_KEY_REUSED',
  'RATE_LIMITED', // 429
  'INTERNAL_ERROR',
  'DATABASE_UNAVAILABLE', // 500/503
] as const;

export type ErrorCode = (typeof ERROR_CODES)[number];

export const errorCodeSchema = z.enum(ERROR_CODES).meta({ id: 'ErrorCode' });

/**
 * The HTTP status each code is returned with. Shared so that the API's exception filter and the
 * web client's error handling cannot disagree about what a code means.
 *
 * Note `NOT_FOUND` for an RLS denial on a write (PostgreSQL `42501`): a denial outside the tenant
 * must never become an existence oracle (§12.4).
 */
export const ERROR_STATUS: Readonly<Record<ErrorCode, number>> = Object.freeze({
  VALIDATION_FAILED: 400,
  INVALID_CURSOR: 400,
  INVALID_HEADER: 400,
  EXPORT_UNKNOWN: 400,
  EXPORT_FORMAT_NOT_ALLOWED: 400,
  EXPORT_SCOPE_NOT_ALLOWED: 400,
  EXPORT_PARAM_MISSING: 400,
  EXPORT_EMPTY: 400,
  EXPORT_FILTER_INVALID: 400,
  EXPORT_TOO_LARGE: 400,
  UNAUTHENTICATED: 401,
  TOKEN_EXPIRED: 401,
  TOKEN_INVALID: 401,
  ACCOUNT_DISABLED: 401,
  PERSON_NOT_PROVISIONED: 401,
  FORBIDDEN: 403,
  CONSENT_REQUIRED: 403,
  NOT_A_MEMBER: 403,
  ORGANIZATION_FROZEN: 403,
  ACCOUNT_CLOSING: 403,
  EMAIL_NOT_VERIFIED: 403,
  AI_ACCESS_DISABLED: 403,
  AI_WRITE_DISABLED: 403,
  EXTERNAL_BLOCKED: 403,
  EXTERNAL_DRAFT_ONLY: 403,
  AI_CONFIRMATION_REQUIRED: 428,
  AI_CONFIRMATION_INVALID: 403,
  AI_ACKNOWLEDGEMENT_REQUIRED: 400,
  EXTERNAL_ORGANIZATION_NOT_GRANTED: 403,
  NOT_FOUND: 404,
  ALREADY_EXISTS: 409,
  CONCURRENT_MODIFICATION: 409,
  ORGANIZATION_REQUIRED: 409,
  LAST_SUPERADMIN: 409,
  IDEMPOTENCY_KEY_REUSED: 409,
  RATE_LIMITED: 429,
  INTERNAL_ERROR: 500,
  DATABASE_UNAVAILABLE: 503,
});

/**
 * `CONSENT_REQUIRED` -> `errors.code.consentRequired` (§14.5 rule 9). The web app renders this
 * key; `check-i18n` asserts one exists in both locale files for every `ErrorCode`.
 */
export function errorCodeI18nKey(code: ErrorCode): string {
  const camel = code
    .toLowerCase()
    .replace(/_([a-z0-9])/g, (_match, char: string) => char.toUpperCase());
  return `errors.code.${camel}`;
}

export const isErrorCode = (value: unknown): value is ErrorCode =>
  typeof value === 'string' && (ERROR_CODES as readonly string[]).includes(value);
